import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags';
import { type BridgeData } from '@databases/database-state/state/bridge-data';
import { perf } from '@databases/logs/performance';

import {
  type ClientFeatureFlagName,
  currentFeatureFlagState,
  getFeatureFlagOverrides,
  isFeatureFlagEnabled,
  setFeatureFlagOverride,
} from '@canvas/client-feature-flags';
import {
  applyExperimentParamOverrides,
  applyFeatureFlagOverridesToExperiments,
} from '@canvas/client-feature-flags/experiments';
import { getExperimentParamOverrides } from '@canvas/client-feature-flags/get-experiment-params-from-url';
import { getPersistedExperimentOverrides } from '@canvas/client-feature-flags/overrides/experiment-persisted';
import { initStatsig } from '@canvas/client-feature-flags/statsig';
import {
  getFrameApi,
  getContentAPI,
} from '@canvas/frame-api/host-api/host-frame-context';
import { setContext } from '@canvas/sentry-react';

const getFeatureFlagsInUse = (): string[] => {
  const featureFlagsInUse: string[] = [];

  for (const key in currentFeatureFlagState) {
    if (Object.hasOwn(currentFeatureFlagState, key)) {
      const flagKey = key as ClientFeatureFlagName;

      if (currentFeatureFlagState[flagKey].enabled && flagKey.startsWith('database')) {
        featureFlagsInUse.push(flagKey);
      }
    }
  }

  return featureFlagsInUse;
};

export const initializeFeatureFlags = async ({
  cloudId,
  userId,
}: Pick<BridgeData, 'cloudId' | 'userId'>) => {
  perf.timeLog('app-initialization', 'feature-flags-start');

  const windowHref = getFrameApi().href;
  const featureFlagOverrides = getFeatureFlagOverrides(windowHref);
  const experimentParamOverrides = getExperimentParamOverrides(windowHref);

  setFeatureFlagOverride(featureFlagOverrides);
  applyFeatureFlagOverridesToExperiments(featureFlagOverrides);

  // Apply persisted experiment overrides from localStorage (set via DevTools UI)
  const persistedExperimentOverrides = getPersistedExperimentOverrides();
  applyExperimentParamOverrides(persistedExperimentOverrides);

  // Apply URL-based overrides on top (URL takes precedence over localStorage)
  applyExperimentParamOverrides(experimentParamOverrides);
  await initStatsig(featureFlagOverrides, {
    accountId: userId,
    tenantId: cloudId,
    orgId: getContentAPI()?.getOrgId?.(),
  });

  const featureFlagsInUse = getFeatureFlagsInUse();

  setBooleanFeatureFlagResolver((flagKey) => {
    if (flagKey === 'platform-dst-lozenge-tag-badge-visual-uplifts') {
      return isFeatureFlagEnabled('platformLozengeTagBadgeVisualUplifts');
    }
    if (flagKey === 'platform-dst-shape-theme-default') {
      return isFeatureFlagEnabled('platformShapeThemeDefault');
    }

    const alwaysOn = [
      'confluence-issue-terminology-refresh',
      'platform-component-visual-refresh',
      // Smart link feature flags
      // Needed to enable the smart link preview action size and hide the blanket
      // Required to remove smart card patch
      // See:
      // https://switcheroo.atlassian.com/ui/gates/f39f84c4-01ec-43a4-a0c1-3b6de5930ce1/key/linking_platform_hide_preview_action_blanket_prop
      // https://switcheroo.atlassian.com/ui/gates/2aed53b3-6766-4834-aafb-15ac38fe9586/key/platform_linking_enable_card_preview_action_size
      'platform_linking_enable_card_preview_action_size',
      'linking_platform_hide_preview_action_blanket_prop',
      // Needed to deprecate LP-CC-EMBED microservice in favor of simply
      // rendering the embedded confluence iframe for smart card embeds
      // See:
      // https://switcheroo.atlassian.com/ui/gates/01bf5d3a-23dd-4a10-99e6-5d90dc5deac8/key/platform_deprecate_lp_cc_embed
      'platform_deprecate_lp_cc_embed',
    ];

    return [...alwaysOn, ...featureFlagsInUse].includes(flagKey);
  });

  if (featureFlagsInUse.length > 0) {
    setContext('Feature Flags', {
      'In Use': featureFlagsInUse,
    });
  }
};
